import axios, { type InternalAxiosRequestConfig } from 'axios'
import qs from 'qs'
import { QueryClient } from '@tanstack/react-query'
import { refreshToken } from '@/features/auth/api/refreshToken'
import { getConfigOptions } from '@/features/auth/api/getConfig'
import type { Store } from 'redux'
import type { AppDispatch, RootState } from '@/app/store/store'
import { authAction } from '@/app/store/actions/authAction'
import { config as appConfig } from '@/config'

let store: Store
let queryClient: QueryClient

export const injectStore = (_store: Store) => {
  store = _store
}

export const injectQueryClient = (_queryClient: QueryClient) => {
  queryClient = _queryClient
}

export const authClient = axios.create({
  baseURL: appConfig.auth.authority,
  withCredentials: true,
})

export const authAdminClient = axios.create({
  baseURL: appConfig.auth.apiUrl,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

export const tokenInjectInterceptor = async (config: InternalAxiosRequestConfig) => {
  if (!store) {
    return config
  }

  const dispatch = store.dispatch as AppDispatch
  const state = store.getState() as RootState
  if (!state.auth.accessToken) {
    return config
  }
  const isAccessTokenExpired = new Date(state.auth.accessTokenExpiration * 1000) < new Date()
  const isRefreshTokenExpired = new Date(state.auth.refreshTokenExpiration * 1000) < new Date()
  if (state.auth.accessToken && !isAccessTokenExpired) {
    config.headers['Authorization'] = `Bearer ${state.auth.accessToken}`
  } else if (state.auth.refreshToken && !isRefreshTokenExpired) {
    if (!queryClient) {
      return config
    }

    try {
      const oidcConfig = await queryClient.ensureQueryData(getConfigOptions())
      const res = await refreshToken({ refreshToken: state.auth.refreshToken, config: oidcConfig })
      await dispatch(authAction({ authResponse: res })).unwrap()
      config.headers['Authorization'] = `Bearer ${res.access_token}`
    } catch {
      window.location.reload()
    }
  } else {
    window.location.reload()
  }

  return config
}

export const createUserNameInterceptor = (headerName = 'X-UserId') => {
  return (config: InternalAxiosRequestConfig) => {
    if (!store) return config

    const state = store.getState() as RootState
    const userName = state.user?.userName

    // ha nem akarod küldeni üresen, akkor csak ha van:
    if (userName) {
      config.headers[headerName] = userName
    } else {
      // opcionális fallback, ha eddig "unknown"-t küldtél:
      config.headers[headerName] = 'unknown'
    }

    return config
  }
}
export const createLocaleInterceptor = (locale: string) => {
  return (config: InternalAxiosRequestConfig) => {
    config.headers['Accept-Language'] = locale
    return config
  }
}
authAdminClient.interceptors.request.use(tokenInjectInterceptor)
